import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Loader, Menu, Segment, Sidebar } from 'semantic-ui-react';
import { isEmpty } from 'lodash';

import { CustomSegment } from '../../../../components';
import { ActivityChecklist } from '../activityChecklist';
import { AggregateReports } from '../aggregateReports';
import { DocumentNotes, FinalActivityDescription } from '../documentationNotes';
import { DocumentAndMedia } from '../documentMedia';
import ContractorsCard from '../ContractorsCard';
import { FinancialSummaryCard } from './FinancialSummary';
import { LinkedSettings } from '../linkedSettings';
import { isParentProgramActivity } from '../../../../utils/activity';
import { getPageDisable } from '../../../../utils';
import ProjectText from '../../../../components/projectText';
import { setParentReportType } from '../../../../store/actions';
import { SIDEBAR_TYPES } from '../../../../utils/constant';

const Documentation = ({
  currentActivity,
  actPut,
  user,
  helpTips,
  ...props
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const parentReportType = useSelector(
    (state) => state.report.parentReportType
  );

  const pageDisabled = getPageDisable(
    currentActivity,
    user,
    'project.edit.documentation'
  );

  const isAdvancedFilter =
    useSelector((state) => state.sidebar.sideBarType) === SIDEBAR_TYPES[2];

  const isVisibleSidebar = useSelector(
    (state) => state.sidebar.isVisibleSidebar
  );
  const advancedToggle = isAdvancedFilter && isVisibleSidebar;

  const [reportType, setReportType] = useState('activities');
  // Defaults true so the card doesn't flash in then out while its own
  // contractor count is still loading — only hidden once confirmed empty.
  const [hasContractors, setHasContractors] = useState(true);

  useEffect(() => {
    if (reportType !== parentReportType) {
      dispatch(setParentReportType(reportType));
    }
  }, [reportType]); // eslint-disable-line react-hooks/exhaustive-deps

  const activityType = isParentProgramActivity(
    currentActivity.activityPlanTypeId
  )
    ? t('common.program').toLowerCase()
    : t('common.activity').toLowerCase();

  return (
    <div>
      <CustomSegment
        className="final-activity-description"
        title={t('activity.documentation.finalDescription.title', {
          type: activityType,
        })}
        subTitle={t('activity.documentation.finalDescription.subtitle', {
          type: activityType,
        })}
        children={
          <FinalActivityDescription
            currentState="project.edit.documentation"
            pageDisabled={pageDisabled}
          />
        }
        disabled={pageDisabled}
        extraMargin
      />

      <CustomSegment
        className="document-and-media"
        title={t('activity.documentation.documents_and_media.title')}
        children={
          <DocumentAndMedia currentState="project.edit.documentation" />
        }
        extraMargin
        disabled={pageDisabled}
      />
      {/* TAK-2086: income and expenses down to the Financial Result — single
          activities (linked included); programs come with their pool design */}
      {!isParentProgramActivity(currentActivity.activityPlanTypeId) && (
        <FinancialSummaryCard
          currentActivity={currentActivity}
          pageDisabled={pageDisabled}
        />
      )}

      {/* ContractorsCard stays mounted even when hidden — it re-fetches on
          activity change, so if a contractor is later added it can show
          itself again without a page reload. Hidden via style, not by
          omitting it from the tree, so that re-fetch keeps happening. */}
      <CustomSegment
        className="contractors-card"
        title={t('common.contractors')}
        style={hasContractors ? undefined : { display: 'none' }}
        children={
          <ContractorsCard
            currentActivity={currentActivity}
            pageDisabled={pageDisabled}
            onHasContractors={setHasContractors}
          />
        }
        extraMargin
        disabled={pageDisabled}
      />

      {isParentProgramActivity(currentActivity.activityPlanTypeId) && (
        <div>
          <CustomSegment
            title={t('activity.documentation.programReports.title')}
            className={
              advancedToggle ? 'program-report open-panel' : 'program-report'
            }
            children={
              <AggregateReports
                currentState="project.edit.documentation"
                setReportType={setReportType}
                visible={advancedToggle}
              />
            }
            tooltip={{
              user: user,
              helpTips: helpTips,
              id: '122',
            }}
            extraMargin
            disabled={pageDisabled}
          />
        </div>
      )}

      <CustomSegment
        className="analysis-and-reflection"
        title={t('activity.documentation.analysisAndReflection.title')}
        subTitleStyle
        subTitle={
          <ProjectText
            defaultText={t(
              'activity.documentation.analysisAndReflection.subtitle'
            )}
            textId={8}
          />
        }
        children={
          <DocumentNotes
            currentState="project.edit.documentation"
            pageDisabled={pageDisabled}
          />
        }
        rightHeader={
          <Loader active={actPut.projectNotes ? true : false} inline />
        }
        extraMargin
        disabled={pageDisabled}
      />

      {!isParentProgramActivity(currentActivity.activityPlanTypeId) &&
        !isEmpty(currentActivity.activityReportCheckList) && (
          <CustomSegment
            title={t('organisation.organization.checklist')}
            children={
              <ActivityChecklist
                documentation
                currentState="project.edit.documentation"
                pageDisabled={pageDisabled}
              />
            }
            rightHeader={
              <Loader
                active={actPut.checklistReport ? actPut.checklistReport : false}
                inline
              />
            }
            extraMargin
            disabled={pageDisabled}
          />
        )}

      {currentActivity.parentId && (
        <CustomSegment
          title={t('common.settings')}
          children={
            <LinkedSettings
              actPut={actPut}
              currentState="project.edit.documentation"
            />
          }
          extraMargin
          disabled={pageDisabled}
        />
      )}
    </div>
  );
};

export default Documentation;
